"use client"

import * as React from "react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import {
  IconArmchair,
  IconArrowBackUp,
  IconCalendarEvent,
  IconCar,
  IconDoorExit,
  IconCircleCheck,
  IconCircleX,
  IconStethoscope,
  IconUserOff,
} from "@tabler/icons-react"

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu"
import { usePermissions } from "@/hooks/use-permissions"
import { updateAppointmentStatus } from "@/lib/appointments-api"
import { checkInAppointment, type CheckinStep } from "@/lib/appointment-workflow-api"
import { sendAppointmentRideToDrivers } from "@/lib/logistics-api"
import { hasFlag } from "@/lib/feature-flags"
import { useAuthStore } from "@/stores/auth-store"
import type { AppointmentStatus } from "@/types"

/**
 * App-wide right-click quick-status menu for an appointment (UNFLAGGED).
 *
 * Covers BOTH appointment dimensions in one menu:
 *  - check-in  → PATCH /appointments/{id}/checkin {step} (arrived / seated /
 *    dismissed / undo). Always on since 85-02 — no flag, no status list.
 *  - status    → PATCH /appointments/{id}/status (scheduled/completed/
 *    cancelled/no_show). The backend keeps ClinicalVisit.occurred_on in sync.
 *
 * Both dimensions require `appointments.edit`; without it the children render
 * bare (no trigger wrapper at all — hide-not-disable).
 */

interface AppointmentQuickActionsValue {
  /** Called after ANY successful quick action, so the surface can refresh. */
  onChanged?: () => void
}

const AppointmentQuickActionsContext =
  React.createContext<AppointmentQuickActionsValue | null>(null)

/**
 * Supplies the refresh callback to deeply-nested appointment chips without
 * prop-drilling it through every calendar view. Mounted by the calendar page
 * around its view area; surfaces that render their own rows pass `onChanged`
 * to the menu directly and need no provider.
 */
export function AppointmentQuickActionsProvider({
  onChanged,
  children,
}: {
  onChanged?: () => void
  children: React.ReactNode
}) {
  const value = React.useMemo<AppointmentQuickActionsValue>(
    () => ({ onChanged }),
    [onChanged]
  )
  return (
    <AppointmentQuickActionsContext.Provider value={value}>
      {children}
    </AppointmentQuickActionsContext.Provider>
  )
}

/** `null` when no provider is mounted — consumers then render children bare. */
export function useAppointmentQuickActions(): AppointmentQuickActionsValue | null {
  return React.useContext(AppointmentQuickActionsContext)
}

export interface AppointmentQuickStatusTarget {
  /** RAW appointment id (never a composite calendar-feed id). */
  id: string
  /** Current status — its menu item renders disabled. */
  status?: string
  /** Appointment INTENT; `null`/absent counts as `clinic_visit` (legacy rows). */
  type?: string | null
}

interface AppointmentQuickStatusMenuProps {
  appointment: AppointmentQuickStatusTarget
  onChanged?: () => void
  children: React.ReactNode
}

export function AppointmentQuickStatusMenu({
  appointment,
  onChanged,
  children,
}: AppointmentQuickStatusMenuProps) {
  const t = useTranslations("appointmentQuickActions")
  const { can } = usePermissions()
  const user = useAuthStore((state) => state.user)
  const [pending, setPending] = React.useState(false)

  // Server middleware (appointments.edit) is authoritative — this is UX only.
  const canEdit = can("appointments.edit")

  // Transport (260907). Three gates, all of which the server enforces again:
  // the module, the pull model, and dispatch rights. `hasFlag` denies on a
  // missing flag, which is the posture that matters here — the route 404s
  // without both flags, and an item that always errors is worse than absent.
  //
  // `logistics.manage`, NOT `.drive`: this is the office putting work in front
  // of the drivers. It is deliberately narrower than `appointments.edit`, so
  // the front desk keeps its check-in menu without gaining dispatch.
  const canSendRide =
    can("logistics.manage") &&
    hasFlag(user?.feature_flags, "logistics_enabled") &&
    hasFlag(user?.feature_flags, "logistics_claim_enabled")

  const run = React.useCallback(
    async (action: () => Promise<unknown>) => {
      if (pending) return
      setPending(true)
      try {
        await action()
        onChanged?.()
      } catch (error) {
        console.error(error)
        toast.error(t("toast.failed"))
      } finally {
        setPending(false)
      }
    },
    [pending, onChanged, t]
  )

  const applyStatus = React.useCallback(
    (status: AppointmentStatus) =>
      void run(async () => {
        await updateAppointmentStatus(appointment.id, status)
        toast.success(t("toast.updated"))
      }),
    [appointment.id, run, t]
  )

  // 85-02: day-of check-in is always on — one PATCH per step.
  const applyCheckIn = React.useCallback(
    (step: CheckinStep) =>
      void run(async () => {
        await checkInAppointment(appointment.id, step)
        toast.success(t("toast.updated"))
      }),
    [appointment.id, run, t]
  )

  // 260907: one press, no dialog — the server derives the whole ride from the
  // appointment. The distinct already-sent toast matters: the endpoint is
  // idempotent, so a second press is a no-op rather than a failure, and saying
  // "sent" again would imply a second car.
  const sendRide = React.useCallback(
    () =>
      void run(async () => {
        const { alreadySent } = await sendAppointmentRideToDrivers(appointment.id)
        alreadySent
          ? toast.info(t("toast.rideAlreadySent"))
          : toast.success(t("toast.rideSent"))
      }),
    [appointment.id, run, t]
  )

  if (!canEdit) {
    return <>{children}</>
  }

  // Check-in only applies to clinic visits; legacy rows carry a null type and
  // are treated as clinic visits everywhere else in the app.
  const showCheckIn =
    appointment.type === "clinic_visit" ||
    appointment.type === null ||
    appointment.type === undefined

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-52">
        {showCheckIn && (
          <>
            <ContextMenuLabel>{t("checkInSection")}</ContextMenuLabel>
            <ContextMenuItem
              disabled={pending}
              onSelect={() => applyCheckIn("arrived")}
            >
              <IconArmchair />
              {t("waitingRoom")}
            </ContextMenuItem>
            <ContextMenuItem
              disabled={pending}
              onSelect={() => applyCheckIn("seated")}
            >
              <IconStethoscope />
              {t("inTreatment")}
            </ContextMenuItem>
            <ContextMenuItem
              disabled={pending}
              onSelect={() => applyCheckIn("dismissed")}
            >
              <IconDoorExit />
              {t("done")}
            </ContextMenuItem>
            <ContextMenuItem
              disabled={pending}
              onSelect={() => applyCheckIn("undo")}
            >
              <IconArrowBackUp />
              {t("undoCheckIn")}
            </ContextMenuItem>
            <ContextMenuSeparator />
          </>
        )}

        <ContextMenuLabel>{t("statusSection")}</ContextMenuLabel>
        <ContextMenuItem
          disabled={pending || appointment.status === "completed"}
          onSelect={() => applyStatus("completed")}
        >
          <IconCircleCheck />
          {t("completed")}
        </ContextMenuItem>
        <ContextMenuItem
          variant="destructive"
          disabled={pending || appointment.status === "cancelled"}
          onSelect={() => applyStatus("cancelled")}
        >
          <IconCircleX />
          {t("cancelled")}
        </ContextMenuItem>
        <ContextMenuItem
          disabled={pending || appointment.status === "no_show"}
          onSelect={() => applyStatus("no_show")}
        >
          <IconUserOff />
          {t("noShow")}
        </ContextMenuItem>
        {/* The undo for a mis-click: any terminal outcome can go back to
            scheduled without opening the edit form. */}
        <ContextMenuSeparator />
        <ContextMenuItem
          disabled={pending || appointment.status === "scheduled"}
          onSelect={() => applyStatus("scheduled")}
        >
          <IconCalendarEvent />
          {t("backToScheduled")}
        </ContextMenuItem>

        {/* Transport. Same `showCheckIn` condition as the section at the top:
            only a clinic visit is somewhere a patient has to be driven TO, and
            a legacy null type counts as one. A cancelled appointment keeps the
            item visible but disabled — the server refuses it, and hiding it on
            a mis-click would look like the feature had vanished. */}
        {canSendRide && showCheckIn && (
          <>
            <ContextMenuSeparator />
            <ContextMenuLabel>{t("transportSection")}</ContextMenuLabel>
            <ContextMenuItem
              disabled={pending || appointment.status === "cancelled"}
              onSelect={sendRide}
            >
              <IconCar />
              {t("sendRide")}
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  )
}
